import {useEffect} from "react";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {css} from "@emotion/react";
import {sub} from "date-fns";
import {assign, isEmpty, map} from "lodash";
import {IQueryValue} from "@web2/ap-request";
import {RequestState} from "@web2/ap-request-state";
import {useFetchOnQueryChange} from "@web2/redux_utils";
import {dateTimeFormat, formatPL} from "@web2/string_utils";
import {BounceLoader, CenterBox} from "@web2/ui_utils";

import {IAPTheme} from "../../app/ap_client";
import {IAPStore} from "../../app/reducers/ap_reducer";
import {df, fai_center, fd_col, fd_row, fg_1, fjc_center, fs_1} from "../../app/styles/flexbox";
import {h100vh, overflow_y_h, tac} from "../../app/styles/helpers";
import {Navigation} from "../../navigation/Navigation";
import {fetchAgentsList} from "../actions/fetch_agent_list";
import {getApplicationList} from "../actions/fetch_applicactions";
import {ApplicationListItem} from "../components/ApplicationListItem";
import {DownloadApplicationsButton} from "../components/DownloadApplicationsButton";
import {EmptyListMessage} from "../components/EmptyListMessage";
import {ApplicationSearchForm} from "../components/SearchForm";
import {IAPApplication} from "../reducers/applications_list_reducer";

const defaultPage = 1;
const defaultSort = "-create_date";

export const ApplicationsView = () => {
    const {list, requestState} = useSelector((store: IAPStore) => store.applications, shallowEqual);
    const dispatch = useDispatch();
    useFetchOnQueryChange((store: IAPStore) => store.applications, getApplicationList, validateQuery);
    useEffect(() => {
        dispatch(fetchAgentsList());
    }, []);

    return (
        <article css={[h100vh, df, fd_col]}>
            <Navigation />

            <section css={[fg_1, fs_1, df, fd_row, overflow_y_h]}>
                <aside css={asideHolder}>
                    <ApplicationSearchForm />
                </aside>

                {requestState === RequestState.Success ? (
                    list.length < 1 ? (
                        <div css={[listHolder, df, fai_center, fjc_center, tac]}>
                            <EmptyListMessage mainMessage="Niestety nic nie znaleźliśmy" subMessage="Zmień parametry wyszukania i spróbuj ponownie" />
                        </div>
                    ) : (
                        <div css={listHolder}>
                            <div css={applicationCountWrapper}>
                                <h1 css={headline}>Zgłoszenia: {list.length}</h1>
                                <DownloadApplicationsButton size="sm" />
                            </div>

                            {map(list, (application: IAPApplication, idx) => (
                                <ApplicationListItem key={idx + application.create_date} application={application} />
                            ))}
                        </div>
                    )
                ) : (
                    <CenterBox>
                        <BounceLoader />
                    </CenterBox>
                )}
            </section>
        </article>
    );
};

let firstValidation = true;
const validateQuery = (query: Record<string, IQueryValue>): Record<string, IQueryValue> => {
    const resultQuery = {} as Record<string, IQueryValue>;
    if (firstValidation === true && isEmpty(query)) {
        resultQuery["create_date_0"] = formatPL(sub(new Date(), {months: 1}), dateTimeFormat.dashedDateFormat);
        resultQuery["create_date_1"] = formatPL(new Date(), dateTimeFormat.dashedDateFormat);
    }
    firstValidation = false;
    return assign(resultQuery, {
        page: query.page || defaultPage.toString(),
        sort: query.sort || defaultSort
    });
};

const asideHolder = (theme: IAPTheme) => css`
    flex-grow: 0;
    flex-shrink: 0;
    width: 27rem;
    background-color: ${theme.colors.gray_warmer};
`;

const headline = css`
    padding: 0;
    margin: 0 2rem 0 0;
`;

const listHolder = css`
    flex-grow: 1;
    flex-shrink: 1;
    padding: 1.5rem;
    overflow-y: auto;
`;

const applicationCountWrapper = css`
    display: flex;
    align-items: center;
`;
